import { takeLatest, put, select, call } from 'redux-saga/effects';
import { getFirebase } from 'react-redux-firebase';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import MailChimpService from 'utils/mailchimpService';
import MixPanel from 'utils/mixpanelService';
import { setCookie } from 'utils/cookieStore';
import { getSavedTopics, saveTopic } from 'containers/Topics/actions';
import { putAssessment } from 'containers/Assessment/actions';
import {
  getSavedAssessments,
  getTakenAssessments,
  saveAssessment,
} from 'containers/Assessments/actions';
import {
  getSavedResources,
  saveResource,
  rateResource,
} from 'containers/Resources/actions';
import { createList } from 'containers/EditorLists/actions';
import {
  SIGN_UP,
  CHECK_EMAIL_AVAILABILITY,
  COMPLETE_SIGN_UP,
  LOGOUT,
  SUBMIT_SUBSCRIPTION,
  GET_SUBSCRIPTION_STATUS,
  UPDATE_SUBSCRIPTION_STATUS,
} from './constants';
import {
  processing,
  authError,
  subscriptionProcessing,
  checkEmailAvailabilityResult,
  submitSubscriptionResult,
  getSubscriptionStatusResult,
  showAuthCTAModal,
} from './actions';
import { makeSelectProfile, makeSelectStoredItem } from './selectors';

function* signUpSaga({ payload }) {
  const { provider = {}, data = {} } = payload;
  let response;
  try {
    yield put(processing(true));
    const isLogin = _isEmpty(data);
    if (isLogin) {
      response = yield getFirebase().login(provider);
    } else {
      const {
        email,
        password,
        firstName,
        lastName,
        gender,
        genderOther,
        dob,
      } = data;
      response = yield getFirebase().createUser(
        {
          email,
          password,
          displayName: `${firstName} ${lastName}`,
        },
        {
          displayName: `${firstName} ${lastName}`,
          gender,
          genderOther,
          dob,
          email,
          resourcesSubscription: data.receiveMarketing,
        },
      );
    }

    const { profile } = response;

    if (
      data.receiveMarketing ||
      (isLogin && provider.provider && profile.resourcesSubscription)
    ) {
      const subscriptionResponse = yield call(() =>
        MailChimpService.getUserStatus(profile.email),
      );
      if (subscriptionResponse.status === 204) {
        const checked = ['news', 'resources'];
        yield call(() => MailChimpService.subscribe(profile.email, checked));
      }
    }

    const shouldUpdateEmail =
      provider && provider.provider === 'facebook' && profile.email;
    if (shouldUpdateEmail) {
      yield getFirebase().updateProfile({ email: profile.email });
    }

    setCookie('isSubscribed', 'true', {
      expires: 365,
    });
    setCookie('signMethod', (provider && provider.provider) || 'email', {
      expires: 365,
    });

    const storedItem = yield select(makeSelectStoredItem());
    if (!_isEmpty(storedItem)) {
      if (storedItem.type === 'resource')
        yield put(saveResource(storedItem.payload));
      else if (storedItem.type === 'topic')
        yield put(saveTopic(storedItem.payload));
      else if (storedItem.type === 'assessment')
        yield put(saveAssessment(storedItem.payload));
      else if (storedItem.type === 'saveToList')
        yield put(createList(storedItem.payload));
      else if (storedItem.type === 'rate')
        yield put(rateResource(storedItem.payload));
      else if (storedItem.type === 'assessmentResult')
        yield put(putAssessment(storedItem.payload));
    }
  } catch (error) {
    let hasError = true;
    if (error.code === 'auth/account-exists-with-different-credential') {
      hasError = false;
      try {
        const getPreviousProvider = yield getFirebase()
          .functions()
          .httpsCallable('getPreviousProvider');
        const providerResponse = yield getPreviousProvider({
          email: error.email,
        });

        const providerName = providerResponse.data.providerId;
        if (providerName !== 'password') {
          const isLogin = _isEmpty(data);
          if (isLogin) MixPanel.track('Login - Failed');
          else MixPanel.track('Sign up - Failed');
          yield put(
            showAuthCTAModal({
              type: 'previous-provider',
              method: providerName,
            }),
          );
        } else {
          hasError = true;
        }
      } catch (e) {
        hasError = true;
      }
    }

    if (hasError) {
      const isLogin = _isEmpty(data);
      if (isLogin) MixPanel.track('Login - Failed');
      else MixPanel.track('Sign up - Failed');
      yield put(authError(error));
    }
  } finally {
    yield put(processing(false));
  }
}

function* completeSignUpSaga({ payload }) {
  try {
    yield put(processing(true));
    const {
      data: { email, marketing },
    } = payload;
    if (!_isEmpty(email)) {
      yield getFirebase()
        .auth()
        .currentUser.updateEmail(email);
      yield getFirebase().updateProfile({
        email,
        resourcesSubscription: marketing,
      });

      MixPanel.track('Email change - Successful');
      MixPanel.people.set({
        $email: email,
        sendResourceUpdates: marketing,
      });

      if (marketing) {
        const checked = ['news', 'resources'];

        yield call(() => MailChimpService.subscribe(email, checked));
      }
    }
  } catch (error) {
    MixPanel.track('Email change - Failed');
  } finally {
    yield put(processing(false));
  }
}

function* logoutSaga() {
  try {
    yield put(processing(true));
    yield MixPanel.track('Logout - Successful');
    yield MixPanel.reset();
    yield getFirebase().logout();
  } finally {
    yield put(processing(false));
    yield put(getSavedTopics());
    yield put(getSavedAssessments());
    yield put(getTakenAssessments());
    yield put(getSavedResources());
  }
}

function* submitSubscriptionSaga({ payload }) {
  try {
    yield put(subscriptionProcessing(true));
    const { email, checked } = payload;

    const methods = yield getFirebase()
      .auth()
      .fetchSignInMethodsForEmail(email);
    if (methods.length > 0) {
      yield put(
        submitSubscriptionResult({
          error: true,
          exists: true,
          errorMessage:
            'This email address is being used for a user account. Do you want to Sign In and manage subscriptions?',
        }),
      );
    } else {
      const response = yield call(() =>
        MailChimpService.subscribe(email, checked),
      );

      if (response.status === 200) {
        MixPanel.people.set({
          $email: email,
          sendResourceUpdates: checked.includes('resources'),
        });
        MixPanel.track('Subscription status added');

        yield put(submitSubscriptionResult({ error: false }));
      } else {
        yield put(
          submitSubscriptionResult({
            error: true,
            exists: false,
            errorMessage: 'Something went wrong. Please, try again!',
          }),
        );
      }
    }
  } catch (error) {
    if (error.status === 400) {
      if (error.data.title === 'Member Exists') {
        yield put(
          submitSubscriptionResult({
            error: true,
            exists: true,
            errorMessage: `${
              error.data.detail.split(' ')[0]
            } is already subscribed.`,
          }),
        );
      } else {
        yield put(
          submitSubscriptionResult({
            error: true,
            exists: true,
            errorMessage: error.data.detail,
          }),
        );
      }
    } else {
      yield put(
        submitSubscriptionResult({
          error: true,
          exists: false,
          errorMessage: 'Something went wrong. Please, try again!',
        }),
      );
    }
  } finally {
    yield put(subscriptionProcessing(false));
  }
}

function* getSubscriptionStatusSaga({ payload }) {
  try {
    yield put(subscriptionProcessing(true));
    const email = payload;
    const response = yield call(() => MailChimpService.getUserStatus(email));
    if (response.status === 200) {
      const { data } = response;
      const { status, mergeFields } = data;

      const checked = Object.keys(mergeFields)
        .filter(el => mergeFields[el] === 1)
        .map(el => el.toLowerCase());

      const profile = yield select(makeSelectProfile());
      if (profile.resourcesSubscription) {
        checked.push('resources');
      }
      yield put(getSubscriptionStatusResult({ status, checked }));
    } else {
      yield put(getSubscriptionStatusResult({ status: '', checked: [] }));
    }
  } catch (error) {
    yield put(getSubscriptionStatusResult({ status: '', checked: [] }));
  } finally {
    yield put(subscriptionProcessing(false));
  }
}

function* updateSubscriptionStatusSaga({ payload }) {
  try {
    yield put(subscriptionProcessing(true));
    const { email, checked } = payload;
    const profile = yield select(makeSelectProfile());

    const oldEmail = _cloneDeep(profile).email;

    const response = yield call(() =>
      MailChimpService.updateSubscription(email, checked, oldEmail),
    );

    if (response.status === 200) {
      try {
        yield getFirebase()
          .auth()
          .currentUser.updateEmail(email);
        yield getFirebase().updateProfile({
          email,
          resourcesSubscription: checked.includes('resources'),
        });
        MixPanel.people.set({
          $email: email,
          sendResourceUpdates: checked.includes('resources'),
        });
        MixPanel.track('Subscription status changed');

        yield put(submitSubscriptionResult({ error: false }));
      } catch (error) {
        yield call(() =>
          MailChimpService.updateSubscription(oldEmail, checked, email),
        );

        yield put(
          submitSubscriptionResult({
            error: true,
            exists: false,
            errorMessage: 'Something went wrong. Please, try again!',
          }),
        );
      }
    } else {
      yield put(
        submitSubscriptionResult({
          error: true,
          exists: false,
          errorMessage: 'Something went wrong. Please, try again!',
        }),
      );
    }
  } catch (error) {
    yield put(
      submitSubscriptionResult({
        error: true,
        exists: false,
        errorMessage: 'Something went wrong. Please, try again!',
      }),
    );
  } finally {
    yield put(subscriptionProcessing(false));
  }
}

function* checkEmailAvailabilitySaga({ payload }) {
  try {
    yield put(processing(true));
    const email = payload;
    const methods = yield getFirebase()
      .auth()
      .fetchSignInMethodsForEmail(email);
    if (methods.length > 0) {
      yield put(
        checkEmailAvailabilityResult({
          exists: true,
        }),
      );
    } else {
      yield put(
        checkEmailAvailabilityResult({
          available: true,
        }),
      );
    }
  } catch (error) {
    yield put(
      checkEmailAvailabilityResult({
        error: true,
      }),
    );
  } finally {
    yield put(processing(false));
  }
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(SIGN_UP, signUpSaga);
  yield takeLatest(CHECK_EMAIL_AVAILABILITY, checkEmailAvailabilitySaga);
  yield takeLatest(COMPLETE_SIGN_UP, completeSignUpSaga);
  yield takeLatest(LOGOUT, logoutSaga);
  yield takeLatest(SUBMIT_SUBSCRIPTION, submitSubscriptionSaga);
  yield takeLatest(GET_SUBSCRIPTION_STATUS, getSubscriptionStatusSaga);
  yield takeLatest(UPDATE_SUBSCRIPTION_STATUS, updateSubscriptionStatusSaga);
}
